// Shared, theme-aware style constants for the Marine Forecast Rail
// (docs/design_handoff_marine_forecast/).
//
// MONO_FONT is scoped narrowly and deliberately: the wind chart's axis
// NUMBERS only (WindSpeedChart.jsx). It comes from the design handoff, where
// the whole marine rail is set in IBM Plex Mono; the port kept the monospace
// treatment exactly where it earns its keep and dropped it everywhere else.
//
// What it earns: the axis numbers are the one place in the rail where digits
// are meant to be read as measurements against a scale — sitting over their
// own positions on a severity track, compared day to day down the week. Even
// tabular figures in Lato don't give the same even digit rhythm, and a "1"
// next to a "16 kt" reads visibly loose. It also signals instrument reading
// rather than prose, which is the whole point of the chart.
//
// It is NOT a general band typeface. Every word in the rail — captions,
// compass points, the "gusts" qualifier — is the app's normal Lato (this app
// ships no Google Fonts; see app/views/layouts/_lato.html.erb). System
// monospace stack rather than Plex: zero network cost, present on every OS.
export const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

// The rail's data type scale — deliberately just two sizes for all text, plus
// one explicit exception. Every `fz` in the rail maps to one of these; none
// are picked ad hoc per component.
export const DATA_FZ = {
  value: '1.05rem', // any real number/word worth reading — matches WeekRow's value/time size
  label: '0.75rem', // everything else: captions, secondary values, meta/detail text
  headline: '2.2rem', // the one big per-card readout (hero temp) — same role as NowTile's big time, just proportioned for a denser layout. The only size outside the two-tier scale, and only used in one place.
}

// One glyph size for every emoji in the rail (band headers, hourly rows, the
// hero) — matches NowTile's own glyph size, so a bigger/smaller emoji is
// never an accidental per-spot choice.
export const GLYPH_FZ = '1.4rem'

// One tracking value for every uppercase caption on a Week card's weather
// band and the wind chart inside it — they had drifted to .05em in some spots
// and .06em in others, close enough to read as sloppy rather than as any
// intended distinction. CurrentConditionsHero deliberately keeps its own
// wider ramp (.08/.1/.16em), where tracking scales with a label's rank in
// that card's own hierarchy.
export const CAPTION_LS = '.06em'

// The text ramp for anything sitting on a Week card's solid TILE band — the
// day weather band and the wind chart inside it. Unlike RAIL_COLORS these
// don't follow the site's light/dark scheme, because the tiles themselves
// don't ("same blues in light + dark" per the app's tile convention).
// Declared once here because the band and the chart had already drifted
// apart — .7 vs .75 white for the same caption role, plus a bespoke #a8c3ce
// for a third — which is exactly the kind of near-miss that reads as sloppy
// rather than as a deliberate hierarchy.
export const TILE_TEXT = {
  primary: '#fff', // the reading itself
  muted: 'rgba(255,255,255,.7)', // structural labels, secondary readings
  faint: 'rgba(255,255,255,.5)', // inline captions on a reading
}

// Data-severity accent colors (light/dark pairs). The mockup's hexes are
// tuned as pale text on a dark navy card; on a light card those same values
// (e.g. seas' #5cc2b8) fall short of legible text contrast, so light mode
// gets a darker shade of the same hue rather than the mock's literal value.
// These encode meaning (this number IS seas / high-temp / precip),
// so — unlike chrome (borders, tile backgrounds) — they stay a fixed color
// family across themes, just shifted for contrast.
export const RAIL_COLORS = {
  dark: {
    seas: '#5cc2b8',
    seasDim: '#5f95a5',
    tempHi: '#f2b441',
    tempLo: '#6f97a3',
    precip: '#4a9fe0',
    textPrimary: '#e6f0f3',
    textMuted: '#7fa9b6',
    textDim: '#5f95a5',
    textFaint: '#4d7a88',
  },
  light: {
    seas: '#0f8f85',
    seasDim: '#5c7c85',
    tempHi: '#a66a08',
    tempLo: '#6b7c82',
    precip: '#1f6fb0',
    textPrimary: '#0a2530',
    textMuted: '#4a6873',
    textDim: '#5c7c85',
    textFaint: '#7a929a',
  },
}

export function railColors(scheme) {
  return RAIL_COLORS[scheme === 'dark' ? 'dark' : 'light']
}
